import { Box, Button, ButtonProps, Card, Fade, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RequestError, useApi } from "../../services";
import { ErrorOnDialog } from "../modals";
import { BarragemImage, BarragemProdutos } from "../../context";
import { Beenhere, Hive, Map } from "@mui/icons-material";
import TriangleIcon from "../mapa/icons/triangle";

export interface BarragemCardData {
  id: string;
  idEmpreendimento: string;
  nome: string;
  produtos: BarragemProdutos;
  images: BarragemImage[];
  nManchas: number;
}

interface Props {
  barragemId: number;
  width: number;
  openOnClick?: boolean;
}


export const BarragemCard: React.FC<Props> = ({
  barragemId,
  width,
  openOnClick = true,
}) => {
  const navigate = useNavigate();
  const api = useApi();

  const theme = useTheme();
  const contrastText = theme.palette.getContrastText(theme.palette.background.paper);

  const [hovered, setHovered] = useState(false);
  const [barragemData, setBarragemData] = useState<BarragemCardData>();
  const [error, setError] = useState<RequestError>();

  const height = 1.3 * width;

  useEffect(() => {
    const getBarragemData = async () => {
      const barragemData = await api.barragem.getBarragemCard(barragemId);
      if ("error" in barragemData) {
        setError(barragemData);
        return;
      }
      setBarragemData(barragemData);
    };
    getBarragemData();
  }, []);

  const onAccess = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, type: string) => {
    ev.stopPropagation();
    ev.preventDefault();

    if (type === "PAEBM") {
      navigate(`/app/${barragemId}`, { replace: true });
    } else if (type === "GISTM") {
      navigate(`/app/GISTM/${barragemId}`, { replace: true });
    } else if (type === "MANCHAS") {
      navigate(`/app/manchas/${barragemId}`, { replace: true });
    }
  };

  const appsDisponiveis = [
    barragemData?.produtos.pae && { nome: "PAEBM", icon: <TriangleIcon /> },
    barragemData?.produtos.gistm && { nome: "GISTM", icon: <Beenhere /> },
    barragemData?.produtos.manchas && { nome: "MANCHAS", icon: <Hive /> },
  ].filter(Boolean) as { nome: string; icon: React.ReactNode; }[];

  return <>

    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    <Card
      onClick={() => setHovered(true)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        maxHeight: "80vh",
        width: `${width}px`,
        position: "relative",
        height: `${height}px`,
        fontSize: `${width / 150}px`,
        cursor: (barragemData && openOnClick) ? "pointer" : undefined,
        '&:hover': { boxShadow: (barragemData && openOnClick) ? 15 : undefined },
      }}>


      {/* Imagem */}
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        position: 'relative',
        width: '100%',
        height: '100%',
      }}>
        {barragemData &&
          <Box style={{
            width: `${width}px`,
            height: `${(barragemData.nManchas > 0 ? 0.8 : 1) * height}px`,
            maxHeight: "80vh",
            display: "flex",
            position: 'relative',
            flexDirection: "column",
          }}>
            {barragemData.images.length > 0 &&
              <img
                src={barragemData.images[Math.floor(Math.random() * barragemData.images.length)].url}
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  objectFit: 'cover',
                }}
              />
            }

            <Box sx={{
              position: 'absolute',
              bottom: '5%',
              left: '5%',
              display: "flex",
              flexDirection: "column",
              width: `${width}px`,
              height: `${height * 0.60}px`,
              overflowX: "auto",
              paddingRight: "14em",
            }}>

              <div style={{ flex: 1 }} />

              <div style={{
                width: "70em",
                height: "1em",
                backgroundColor: theme.palette.primary.main,
              }} />

              <Typography
                fontWeight={"bold"}
                fontSize={24}
                color="background.paper"
                style={{
                  textOverflow: "ellipsis",
                  textShadow: `2px 2px 4px ${contrastText}`,
                }}
              >
                {barragemData.nome}
              </Typography>
            </Box>
          </Box>
        }
      </Box>

      <Fade in={hovered} timeout={300}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {appsDisponiveis.length > 0 ? (
            <Stack direction="column" spacing={2}>
              {appsDisponiveis.map((app) => (
                <Button
                  key={app.nome}
                  variant="contained"
                  startIcon={app.icon}
                  onClick={(ev) => onAccess(ev, app.nome)}
                  sx={{ fontWeight: 600, px: 2.5, py: 1 }}
                >
                  {app.nome}
                </Button>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="grey.300" sx={{ fontStyle: "italic" }}>
              Sem acesso
            </Typography>
          )}
        </Box>
      </Fade>
    </Card >
  </>;
};